import React, {
  createContext,
  useContext,
  useMemo,
  PropsWithChildren,
} from 'react'

import { useRouter } from 'next/router'

type PageContextProps = {
  darkMode: boolean
  notTranslated: boolean
  redirect404: boolean
  dynamicPages: string[]
}

type PageContextProviderProps = PropsWithChildren

const PageContext = createContext<PageContextProps>({} as PageContextProps)

const lightPages = ['/culture', '/case/[id]', '/', '/blog', '/blog/[id]']
const notTranslatePages = ['/case/[id]', '/blog', '/blog/[id]']
const dynamicPages = ['/case/', '/blog/']

export const PageContextProvider: React.FC<PageContextProviderProps> = ({
  children,
}) => {
  const router = useRouter()

  const darkMode = useMemo(
    () => !lightPages.includes(router.pathname),
    [router],
  )
  const notTranslated = useMemo(
    () => notTranslatePages.includes(router.pathname),
    [router],
  )

  const redirect404 = useMemo(
    () => dynamicPages.some((d) => router.asPath.includes(d)),

    [router.asPath],
  )

  const contextProps = useMemo(
    () => ({
      darkMode,
      notTranslated,
      redirect404,
      dynamicPages,
    }),
    [darkMode, notTranslated, redirect404],
  )

  return (
    <PageContext.Provider value={contextProps}>{children}</PageContext.Provider>
  )
}

export const usePageContext = () => {
  const context = useContext(PageContext)

  if (!context) {
    throw new Error('You just can access this context inside a provider')
  }

  return context
}
